// Per-tab dwell tracking for the staff self-view audit trail
// (staff-food-at-house): Freddi wants the activity feed to show WHICH page a
// staff member is on — Today or Shifts — not just that they signed in. Pure
// segment tracker (unit-tested); the caller feeds it a clock so nothing here
// touches Date directly.

import { STAFF_PAGE_LABELS, type StaffTab } from '@bbbk/schema';

export type { StaffTab };

export interface DwellSegment {
  label: string; // a STAFF_PAGE_LABELS value
  durationMs: number;
}

export const tabLabel = (tab: StaffTab): string => STAFF_PAGE_LABELS[tab];

// The dwell label for what is RENDERED: the Shifts tab records itself, the
// Daily tab records the day actually on screen. Deliberately takes the
// EFFECTIVE day (after the look-ahead clamp), not the clicked one — the
// rendered day can change without a click when a revalidation collapses the
// look-ahead, and the feed must follow the screen, not the last click.
export const dwellTabFor = (tab: 'daily' | 'shifts' | 'info', activeDay: Exclude<StaffTab, 'shifts' | 'info'>): StaffTab =>
  tab === 'daily' ? activeDay : tab;

// Tracks the active tab; switch() closes the current segment and returns it,
// flush() closes whatever is open (leave/unmount) exactly once.
export function makeTabDwell(tab: StaffTab, now: () => number) {
  let current: StaffTab = tab;
  let since = now();
  let flushed = false;
  return {
    switch(next: StaffTab): DwellSegment | null {
      if (flushed || next === current) return null;
      const seg = { label: tabLabel(current), durationMs: now() - since };
      current = next;
      since = now();
      return seg;
    },
    flush(): DwellSegment | null {
      if (flushed) return null;
      flushed = true;
      return { label: tabLabel(current), durationMs: now() - since };
    },
  };
}
